import { Container, Grid, Text, Anchor, Group, Divider, Box } from '@mantine/core';
import { IconBrandTwitter, IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconHeart, IconMail, IconBrandAppstore } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import classes from './Footer.module.css';
import { useMedplum } from '@medplum/react';

import type { JSX } from "react";

export function Footer(): JSX.Element {
  const medplum = useMedplum();
  const isAuthenticated = medplum.getActiveLogin() !== undefined;
  
  return (
    <footer className={classes.footer}>
      <Container size="xl" py="xl">
        <Grid>
          {/* Company Info */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Group gap="xs" mb="md">
              <IconHeart size={24} color="#F4B6A1" />
              <Text size="xl" fw={700}>
                FeelHeard<span style={{ fontSize: '0.9em', color: '#C2A4D6' }}>.me</span>
              </Text>
            </Group>
            <Text size="sm" c="dimmed" mb="lg">
              Your AI companion for empathetic listening and emotional support, available 24/7.
            </Text>
            <Group gap="md">
              <Anchor href="#" c="dimmed" style={{ '&:hover': { color: '#F4B6A1' } }}>
                <IconBrandTwitter size={20} stroke={1.5} />
              </Anchor>
              <Anchor href="#" c="dimmed" style={{ '&:hover': { color: '#F4B6A1' } }}>
                <IconBrandFacebook size={20} stroke={1.5} />
              </Anchor>
              <Anchor href="#" c="dimmed" style={{ '&:hover': { color: '#F4B6A1' } }}>
                <IconBrandInstagram size={20} stroke={1.5} />
              </Anchor>
              <Anchor href="#" c="dimmed" style={{ '&:hover': { color: '#F4B6A1' } }}>
                <IconBrandLinkedin size={20} stroke={1.5} />
              </Anchor>
            </Group>
          </Grid.Col>

          {/* Quick Links */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Text fw={600} mb="md">Quick Links</Text>
            <Box component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Home
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/reflection" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Reflection Guide
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                {isAuthenticated ? (
                  <Anchor component={Link} to="/settings/subscription" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                    Pricing
                  </Anchor>
                ) : (
                  <Anchor href="#pricing" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                    Pricing
                  </Anchor>
                )}
              </Box>
              <Box component="li" mb="xs">
                <Anchor href="#testimonials" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Testimonials
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/faq" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  FAQ
                </Anchor>
              </Box>
            </Box>
          </Grid.Col>

          {/* Legal */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Text fw={600} mb="md">Legal</Text>
            <Box component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/terms" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Terms of Service
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/privacy" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Privacy Policy
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/cookies" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Cookie Policy
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/ai-ethics" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  AI Ethics Statement
                </Anchor>
              </Box>
              <Box component="li" mb="xs">
                <Anchor component={Link} to="/data-protection" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                  Data Protection
                </Anchor>
              </Box>
            </Box>
          </Grid.Col>

          {/* Contact */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Text fw={600} mb="md">Contact Us</Text>
            <Group gap="xs" mb="xs">
              <IconMail size={20} color="#F4B6A1" />
              <Anchor href="mailto:support@somatopia.com" c="dimmed" style={{ '&:hover': { color: '#B3D9E6' } }}>
                support@somatopia.com
              </Anchor>
            </Group>
            
            {/* Mobile App Download */}
            <Text fw={600} mb="md" mt="xl">Download Our App</Text>
            <Group gap="xs" mb="md" align="center" justify="center" className="sm:justify-start">
              <Anchor
                href="https://apps.apple.com/app/feelheard/id6746776325?platform=iphone"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img 
                  src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  style={{ height: '36px' }}
                />
              </Anchor>
              <Anchor
                href="https://play.google.com/store/apps/details?id=me.feelheard"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img 
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  style={{ height: '52px' }}
                />
              </Anchor>
            </Group>
          </Grid.Col>
        </Grid>

        <Divider my="xl" />
        
        <Group justify="space-between" wrap="wrap">
          <Text c="dimmed" size="sm">
            &copy; {new Date().getFullYear()} FeelHeard.me. All rights reserved.
          </Text>
          <Text c="dimmed" size="sm" style={{ maxWidth: 500 }}>
            FeelHeard.me is not a replacement for professional mental health services. 
            If you are in crisis, please call your local emergency services or crisis helpline.
          </Text>
        </Group>
      </Container>
    </footer>
  );
}